import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
// Global styles that used to arrive through page imports when every page was bundled at startup.
// Pages now load on demand, so they are imported here to apply to every page as before.
import './handle-head-list.css';
import './settings-panel.css';
import './zatca-modal.css';
import './payment-panel.css';
import './product-form.css';
import 'react-toastify/dist/ReactToastify.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";

import { Provider, useSelector } from 'react-redux';
import {store} from './store'
import { queryClient } from "./utils/queryClient";
import { appStorageKey } from './utils/global';
import axios from 'axios';

import global_en from './translations/en/global.json' 
import settings_en from './translations/en/settings.json' 
import global_ar from './translations/ar/global.json' 
import settings_ar from './translations/ar/settings.json' 
import i18next from 'i18next';
import { I18nextProvider } from 'react-i18next';

const normalizeLang = (value) => String(value || '').toLowerCase().startsWith('ar') ? 'ar' : 'en';

const I18nWrapper = () => {
  const [i18nReady, setI18nReady] = useState(false);
  const currentLang = useSelector((state) => state.lang.value);

  // Set initial direction immediately to prevent flicker
  useEffect(() => {
    const initialLang = currentLang || 'en';
    const dir = initialLang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.dir = dir;
    document.documentElement.lang = initialLang;
    document.body.dir = dir;
    document.body.classList.remove('rtl', 'ltr');
    document.body.classList.add(dir);
  }, []);

  useEffect(() => {
    const initI18n = async () => {
      const getInitialLang = () => {
        try {
          return localStorage.getItem('preferredLang') || localStorage.getItem('i18nextLng') || localStorage.getItem('appLang') || 'en';
        } catch {
          return 'en';
        }
      };
      const initialLang = normalizeLang(getInitialLang());
      
      await i18next.init({
        interpolation: { escapeValue: false },
        lng: currentLang || initialLang,
        resources: {
          en: {
            global: global_en,
            settings: settings_en,
          },
          ar: {
            global: global_ar,
            settings: settings_ar,
          },
        },
        fallbackLng: 'en',
        detection: {
          order: ['navigator', 'htmlTag', 'cookie', 'localStorage'],
        },
        react: {
          useSuspense: false
        }
      });
      setI18nReady(true);
    };
    initI18n();
  }, []);

  useEffect(() => {
    if (i18nReady) {
      const nextLang = currentLang || 'en';
      i18next.changeLanguage(nextLang);
      const dir = nextLang === 'ar' ? 'rtl' : 'ltr';
      document.documentElement.dir = dir;
      document.documentElement.lang = nextLang;
      document.body.dir = dir;
      document.body.classList.remove('rtl', 'ltr');
      document.body.classList.add(dir);
    }
  }, [currentLang, i18nReady]);

  if (!i18nReady) return null;

  return (
    <I18nextProvider i18n={i18next}>
      <App />
    </I18nextProvider>
  );
};

const safeParse = (value) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const getBranchIds = () => {
  const tenant = safeParse(localStorage.getItem('tenant'));
  const companies = tenant?.companies || [];
  const branchIds = companies.flatMap((company) => company?.branches || [])
    .map((branch) => branch?._id)
    .filter(Boolean);
  return new Set(branchIds);
};

const getCompanyIds = () => {
  const tenant = safeParse(localStorage.getItem('tenant'));
  const companies = tenant?.companies || [];
  const companyIds = companies.map((company) => company?._id).filter(Boolean);
  return new Set(companyIds);
};

const getCurrentBranchId = () => {
  const state = store?.getState?.();
  const storeBranchId = state?.currentBranch?.value;
  if (storeBranchId) return storeBranchId;
  const storedBranch = safeParse(localStorage.getItem(appStorageKey('selectedBranch')));
  return storedBranch?.value;
};

const getCurrentCompanyId = () => {
  const state = store?.getState?.();
  const storeCompanyId = state?.currentCompany?.value;
  if (storeCompanyId) return storeCompanyId;
  const storedCompany = safeParse(localStorage.getItem(appStorageKey('selectedCompany')));
  return storedCompany?.value;
};

const replaceIdsInUrl = (url, branchIds, currentBranchId, companyIds, currentCompanyId) => {
  if (!url) return url;
  try {
    const parsed = new URL(url, window.location.origin);
    const nextPath = parsed.pathname
      .split('/')
      .map((segment) => {
        if (branchIds.has(segment)) return currentBranchId;
        if (companyIds.has(segment)) return currentCompanyId;
        return segment;
      })
      .join('/');
    if (/^https?:\/\//i.test(url)) {
      return `${parsed.origin}${nextPath}${parsed.search}${parsed.hash}`;
    }
    return `${nextPath}${parsed.search}${parsed.hash}`;
  } catch {
    const [path, rest] = url.split(/(?=[?#])/);
    const nextPath = path
      .split('/')
      .map((segment) => {
        if (branchIds.has(segment)) return currentBranchId;
        if (companyIds.has(segment)) return currentCompanyId;
        return segment;
      })
      .join('/');
    return `${nextPath}${rest || ''}`;
  }
};

const updateIdsInObject = (obj, currentBranchId, currentCompanyId) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (currentBranchId) {
    if (obj.branch_id) obj.branch_id = currentBranchId;
    if (obj.branchId) obj.branchId = currentBranchId;
    if (obj.branch?.value) obj.branch.value = currentBranchId;
  }
  if (currentCompanyId) {
    if (obj.company_id) obj.company_id = currentCompanyId;
    if (obj.companyId) obj.companyId = currentCompanyId;
    if (obj.company?.value) obj.company.value = currentCompanyId;
  }
  return obj;
};

axios.interceptors.request.use((config) => {
  const currentBranchId = getCurrentBranchId();
  const currentCompanyId = getCurrentCompanyId();
  if (!currentBranchId && !currentCompanyId) return config;
  const branchIds = getBranchIds();
  const companyIds = getCompanyIds();
  if (config.url && (branchIds.size > 0 || companyIds.size > 0)) {
    config.url = replaceIdsInUrl(
      config.url,
      branchIds,
      currentBranchId,
      companyIds,
      currentCompanyId
    );
  }
  if (config.params && !(config.params instanceof URLSearchParams)) {
    config.params = updateIdsInObject({ ...config.params }, currentBranchId, currentCompanyId);
  }
  if (config.data && !(config.data instanceof FormData)) {
    if (typeof config.data === 'string') {
      try {
        const parsedData = JSON.parse(config.data);
        const nextData = updateIdsInObject(parsedData, currentBranchId, currentCompanyId);
        config.data = JSON.stringify(nextData);
      } catch {
      }
    } else {
      updateIdsInObject(config.data, currentBranchId, currentCompanyId);
    }
  }
  return config;
});

const Root = () => (
  // <React.StrictMode>
    <BrowserRouter>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <I18nWrapper />
        </QueryClientProvider>
      </Provider>
    </BrowserRouter>
  // </React.StrictMode>
);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<Root />);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
